/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  CardManagementProtectedRoute,
  CardManagementProvider,
  CARDS_ROUTE_PERMISSION,
  CardsRouter as BaseCardsRouter,
  ManageCardsSkeleton,
} from "@farmlands/card-management";
import { AppError } from "@farmlands/ui-library";
import { FC } from "react";
import { Navigate } from "react-router-dom";

import { PageContainer } from "../../components/layout/PageContainer";
import { useThemeBackgroundColor } from "../../hooks/useThemeBackgroundColor";
import { HOME_ROUTE } from "../../util/routing";

import { useCardManagementContextValue } from "./hooks/useCardManagementContextValue";

export const CardsRouter: FC = () => {
  useThemeBackgroundColor((theme) => theme.colors.shade0);

  const { contextValue, isLoading, error, refetch } = useCardManagementContextValue();

  if (isLoading) {
    return (
      <PageContainer>
        <ManageCardsSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <AppError contactUrl={contextValue.config.farmlandsContactEmail} onTryAgainClick={refetch} />
    );
  }

  if (!contextValue.identity.selectedAccountNumber) {
    return <Navigate to={HOME_ROUTE} replace />;
  }

  return (
    <CardManagementProvider value={contextValue}>
      <CardManagementProtectedRoute
        {...CARDS_ROUTE_PERMISSION}
        redirectTo={HOME_ROUTE}
        loadingFallback={
          <PageContainer>
            <ManageCardsSkeleton />
          </PageContainer>
        }
      >
        <PageContainer>
          <BaseCardsRouter />
        </PageContainer>
      </CardManagementProtectedRoute>
    </CardManagementProvider>
  );
};

export default CardsRouter;
